/**
 * OIDC per-user storage helpers for TrustShield tokens in ReactShell.
 *
 * Storage is partitioned per authenticated user by their OIDC `sub` claim:
 *   ts-<sub>:oidc.user:<authority>:trustshield
 *
 * Token minting is owned by `initTrustShieldSSO` — it POSTs to
 * `/oidc/trusted-token` with the UV SessionID, and OidcIssuer validates
 * the cookie binding via the imported `checkSession` lib. This module is
 * read-only: it caches the sid→sub mapping (for sync callers like
 * `buildTrustShieldUrl`) and exposes a UserManager per sub for reading
 * stored tokens.
 */
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';

const CLIENT_ID = 'trustshield';

const _managers = new Map<string, UserManager>();
const _sidToSub = new Map<string, string>();

function buildManager(sub: string): UserManager {
    return new UserManager({
        authority: `${window.location.origin}/oidc`,
        client_id: CLIENT_ID,
        redirect_uri: `${window.location.origin}/ts/cb`,
        scope: 'openid profile ts:read ts:write',
        response_type: 'code',
        userStore: new WebStorageStateStore({
            store: window.localStorage,
            prefix: `ts-${sub}:oidc.`,
        }),
        // Refresh is driven on-demand by initTrustShieldSSO / 401 handlers.
        automaticSilentRenew: false,
    });
}

export function getUserManager(sub: string): UserManager {
    const cached = _managers.get(sub);
    if (cached) return cached;
    const mgr = buildManager(sub);
    _managers.set(sub, mgr);
    return mgr;
}

/**
 * Per-tab in-memory cache of `uv-sid → sub` mappings. Populated by
 * `initTrustShieldSSO` after a successful /trusted-token exchange.
 * Sync callers (buildTrustShieldUrl, the `topWindowGetTrustShieldSub`
 * compat bridge) read through this.
 */
export function getCachedSubForSid(sid: string): string | null {
    return _sidToSub.get(sid) ?? null;
}

export function cacheSubForSid(sid: string, sub: string): void {
    _sidToSub.set(sid, sub);
}
